import * as React from 'react';
import { attachHardwareAutocomplete } from '../hardware-autocomplete';

type BulkBenchmarkEntryForm = {
    typeId: number | string;
    result: string;
    validationUrl: string;
};

type BulkBenchmarkAddForm = {
    cpuName: string;
    cpuFrequency: string;
    cpuBusFrequency: string;
    cpuVoltage: string;
    cpuTemperature: string;
    cpuIsEngineeringSample: boolean;
    cpuUnlockedCores: string;
    cpuCount: string;
    memorySize: string;
    memoryModules: string;
    memoryFrequency: string;
    memoryTimings: string;
    memoryType: string;
    gpuName: string;
    gpuCount: string;
    motherboardName: string;
    motherboardVendor: string;
    motherboardBios: string;
    osName: string;
    osVersion: string;
    benchmarks: BulkBenchmarkEntryForm[];
};

type SelectOptionRow = {
    value: string;
    label: string;
};

type BulkBenchmarkAddPage = {
    heading: string;
    backUrl: string;
    postUrl: string;
    submitLabel: string;
    cpuLookupUrl: string;
    gpuLookupUrl: string;
    antiForgeryToken: string;
    errorMessage?: string | null;
    benchmarkTypes: SelectOptionRow[];
    form: BulkBenchmarkAddForm;
};

type BulkRow = {
    key: string;
    typeId: string;
    result: string;
    validationUrl: string;
};

type HardwareField = 'cpuName' | 'gpuName';

type FormFieldProps = {
    label: string;
    field: keyof BulkBenchmarkAddForm;
    name: string;
    value: string;
    type?: 'text' | 'number' | 'url';
    step?: string;
    required?: boolean;
    onChange: (field: keyof BulkBenchmarkAddForm, value: string) => void;
};

type HardwareAutocompleteProps = {
    label: string;
    field: HardwareField;
    name: 'CpuName' | 'GpuName';
    value: string;
    required?: boolean;
    lookupUrl: string;
    onChange: (field: HardwareField, value: string) => void;
};

function createRow(entry?: BulkBenchmarkEntryForm): BulkRow {
    const typeIdValue = entry?.typeId;
    const typeId = typeIdValue == null || typeIdValue === 0 || typeIdValue === '0' ? '' : String(typeIdValue);

    return {
        key: crypto.randomUUID(),
        typeId,
        result: entry?.result ?? '',
        validationUrl: entry?.validationUrl ?? ''
    };
}

function FormField({ label, field, name, value, type = 'text', step, required, onChange }: FormFieldProps) {
    return (
        <div className="form-field">
            <label>
                {label}
                <span className="field-control">
                    <input
                        name={name}
                        type={type}
                        value={value}
                        step={step}
                        required={required}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(field, event.target.value)}
                    />
                </span>
            </label>
        </div>
    );
}

function HardwareAutocomplete({ label, field, name, value, required, lookupUrl, onChange }: HardwareAutocompleteProps) {
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    React.useEffect(() => {
        if (inputRef.current === null) {
            return undefined;
        }

        return attachHardwareAutocomplete(inputRef.current, {
            lookupUrl,
            onSelect: (item) => onChange(field, item.label)
        });
    }, [field, lookupUrl, onChange]);

    return (
        <div className="form-field">
            <label>
                {label}
                <span className="field-control">
                    <input
                        ref={inputRef}
                        name={name}
                        type="text"
                        value={value}
                        required={required}
                        autoComplete="off"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(field, event.target.value)}
                    />
                </span>
            </label>
        </div>
    );
}

export function BulkBenchmarkAdd({ page }: { page: BulkBenchmarkAddPage }) {
    const [form, setForm] = React.useState<BulkBenchmarkAddForm>(page.form);
    const [rows, setRows] = React.useState<BulkRow[]>(() => {
        const entries = page.form.benchmarks ?? [];
        return entries.length > 0 ? entries.map((entry) => createRow(entry)) : [createRow()];
    });
    const [screenshotNames, setScreenshotNames] = React.useState<Record<string, string>>({});

    const updateField = React.useCallback((field: keyof BulkBenchmarkAddForm, value: string): void => {
        setForm((current) => ({
            ...current,
            [field]: value
        }));
    }, []);

    const updateRow = React.useCallback((index: number, field: keyof Omit<BulkRow, 'key'>, value: string): void => {
        setRows((current) => current.map((row, rowIndex) => (
            rowIndex === index
                ? { ...row, [field]: value }
                : row
        )));
    }, []);

    return (
        <form action={page.postUrl} method="post" encType="multipart/form-data">
            <input type="hidden" name="__RequestVerificationToken" value={page.antiForgeryToken} />

            {page.errorMessage ? <div className="alert">{page.errorMessage}</div> : null}

            <h2>CPU</h2>
            <HardwareAutocomplete label="CPU" field="cpuName" name="CpuName" value={form.cpuName} required lookupUrl={page.cpuLookupUrl} onChange={updateField} />
            <FormField label="CPU frequency (MHz)" field="cpuFrequency" name="CpuFrequency" value={form.cpuFrequency} type="number" step="any" required onChange={updateField} />
            <FormField label="CPU bus frequency (MHz)" field="cpuBusFrequency" name="CpuBusFrequency" value={form.cpuBusFrequency} type="number" step="any" onChange={updateField} />
            <FormField label="CPU voltage (V)" field="cpuVoltage" name="CpuVoltage" value={form.cpuVoltage} type="number" step="any" onChange={updateField} />
            <FormField label="CPU temperature (°C)" field="cpuTemperature" name="CpuTemperature" value={form.cpuTemperature} type="number" step="any" onChange={updateField} />
            <FormField label="Unlocked cores" field="cpuUnlockedCores" name="CpuUnlockedCores" value={form.cpuUnlockedCores} type="number" step="1" onChange={updateField} />
            <FormField label="CPU count" field="cpuCount" name="CpuCount" value={form.cpuCount} type="number" step="1" required onChange={updateField} />
            <div className="form-field">
                <label>
                    <span className="field-control field-control--checkbox">
                        <input
                            name="CpuIsEngineeringSample"
                            type="checkbox"
                            value="true"
                            checked={form.cpuIsEngineeringSample}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setForm((current) => ({ ...current, cpuIsEngineeringSample: event.target.checked }))}
                        />
                        Engineering sample
                    </span>
                </label>
                <input name="CpuIsEngineeringSample" type="hidden" value="false" />
            </div>

            <h2>GPU</h2>
            <HardwareAutocomplete label="GPU" field="gpuName" name="GpuName" value={form.gpuName} lookupUrl={page.gpuLookupUrl} onChange={updateField} />
            <FormField label="GPU count" field="gpuCount" name="GpuCount" value={form.gpuCount} type="number" step="1" required onChange={updateField} />

            <h2>RAM</h2>
            <FormField label="Memory size (MB)" field="memorySize" name="MemorySize" value={form.memorySize} type="number" step="1" required onChange={updateField} />
            <FormField label="Memory modules" field="memoryModules" name="MemoryModules" value={form.memoryModules} type="number" step="1" required onChange={updateField} />
            <FormField label="Memory frequency (MHz)" field="memoryFrequency" name="MemoryFrequency" value={form.memoryFrequency} type="number" step="any" required onChange={updateField} />
            <FormField label="Memory timings" field="memoryTimings" name="MemoryTimings" value={form.memoryTimings} onChange={updateField} />
            <FormField label="Memory type" field="memoryType" name="MemoryType" value={form.memoryType} required onChange={updateField} />

            <h2>System</h2>
            <FormField label="Motherboard vendor" field="motherboardVendor" name="MotherboardVendor" value={form.motherboardVendor} onChange={updateField} />
            <FormField label="Motherboard name" field="motherboardName" name="MotherboardName" value={form.motherboardName} onChange={updateField} />
            <FormField label="BIOS version" field="motherboardBios" name="MotherboardBios" value={form.motherboardBios} onChange={updateField} />

            <h2>Software</h2>
            <FormField label="Operating system" field="osName" name="OsName" value={form.osName} required onChange={updateField} />
            <FormField label="Operating system version" field="osVersion" name="OsVersion" value={form.osVersion} onChange={updateField} />

            <h2>Benchmarks</h2>
            {rows.map((row, index) => (
                <fieldset key={row.key}>
                    <legend>Benchmark {index + 1}</legend>
                    <div className="form-field">
                        <label>
                            Benchmark
                            <span className="field-control">
                                <select
                                    name={`Benchmarks[${index}].TypeId`}
                                    value={row.typeId}
                                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => updateRow(index, 'typeId', event.target.value)}>
                                    <option value="">Select benchmark</option>
                                    {page.benchmarkTypes.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </span>
                        </label>
                    </div>
                    <div className="form-field">
                        <label>
                            Score
                            <span className="field-control">
                                <input
                                    name={`Benchmarks[${index}].Result`}
                                    type="number"
                                    step="any"
                                    value={row.result}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => updateRow(index, 'result', event.target.value)}
                                />
                            </span>
                        </label>
                    </div>
                    <div className="form-field">
                        <label>
                            Validation URL
                            <span className="field-control">
                                <input
                                    name={`Benchmarks[${index}].ValidationUrl`}
                                    type="url"
                                    value={row.validationUrl}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => updateRow(index, 'validationUrl', event.target.value)}
                                />
                            </span>
                        </label>
                    </div>
                    <div className="form-field">
                        <label>
                            Screenshot
                            <span className="field-control">
                                <input
                                    name={`Benchmarks[${index}].ScreenshotFile`}
                                    type="file"
                                    accept="image/png,image/jpeg"
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        const fileName = event.target.files?.item(0)?.name ?? '';
                                        setScreenshotNames((current) => ({ ...current, [row.key]: fileName }));
                                    }}
                                />
                            </span>
                        </label>
                        <div className="field-help">{screenshotNames[row.key] || 'No screenshot uploaded.'}</div>
                    </div>
                    <div className="row button-row">
                        <button
                            type="button"
                            onClick={() => {
                                setRows((current) => current.filter((_, rowIndex) => rowIndex !== index));
                                setScreenshotNames((current) => {
                                    const next = { ...current };
                                    delete next[row.key];
                                    return next;
                                });
                            }}
                            disabled={rows.length === 1}>
                            Remove benchmark
                        </button>
                    </div>
                </fieldset>
            ))}
            <div className="row button-row">
                <button type="button" onClick={() => setRows((current) => [...current, createRow()])}>Add benchmark</button>
            </div>

            <button type="submit">{page.submitLabel}</button>
        </form>
    );
}
